import React from 'react';

const STORAGE_KEY = 'apex_journal';

const TAGS = [
  { id: 'lesson',    label: 'Lesson Learned', color: '#00D4FF' },
  { id: 'win',       label: 'Setup Worked',   color: '#00d464' },
  { id: 'loss',      label: 'Stop Hit',       color: '#ff4444' },
  { id: 'emotional', label: 'Emotional Trade', color: '#f59e0b' },
  { id: 'review',    label: 'Needs Review',   color: '#a78bfa' },
  { id: 'general',   label: 'General',        color: '#4a6080' },
];

const MOODS = ['😊','😐','😟','😤','🎯','💡'];

function loadEntries() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'object') {
      return Object.entries(parsed).map(([date, text], i) => ({
        id: Date.now() + i,
        date,
        createdAt: date,
        updatedAt: date,
        text: typeof text === 'string' ? text : JSON.stringify(text),
        tags: [],
        mood: null,
        edited: false,
      }));
    }
    return [];
  } catch { return []; }
}

function saveEntries(entries) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(entries)); } catch {}
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday:'short', month:'short', day:'numeric',
      year:'numeric', hour:'2-digit', minute:'2-digit'
    });
  } catch { return iso || ''; }
}

export default function ApexJournal({ C }) {
  const clr = C || {};
  const dark   = clr.dark   || '#0A0F1E';
  const panel  = clr.panel  || '#0D1526';
  const border = clr.border || 'rgba(0,212,255,0.12)';
  const cyan   = clr.accent || clr.cyan || '#00D4FF';
  const blue   = clr.blue   || '#0066FF';
  const text   = clr.text   || '#E8EAF0';
  const muted  = clr.muted  || '#4A5568';

  const [entries,    setEntries]    = React.useState(loadEntries);
  const [editId,     setEditId]     = React.useState(null);
  const [editText,   setEditText]   = React.useState('');
  const [editTags,   setEditTags]   = React.useState([]);
  const [editMood,   setEditMood]   = React.useState(null);
  const [newText,    setNewText]    = React.useState('');
  const [newTags,    setNewTags]    = React.useState([]);
  const [newMood,    setNewMood]    = React.useState(null);
  const [showNew,    setShowNew]    = React.useState(false);
  const [filter,     setFilter]     = React.useState('all');
  const [search,     setSearch]     = React.useState('');
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [saved,      setSaved]      = React.useState(false);
  const editRef = React.useRef(null);

  React.useEffect(() => { saveEntries(entries); }, [entries]);
  React.useEffect(() => { if (editId && editRef.current) editRef.current.focus(); }, [editId]);

  function addEntry() {
    if (!newText.trim()) return;
    const e = {
      id: Date.now(), date: new Date().toISOString(),
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      text: newText.trim(), tags: newTags, mood: newMood, edited: false,
    };
    setEntries(p => [e, ...p]);
    setNewText(''); setNewTags([]); setNewMood(null); setShowNew(false);
  }

  function startEdit(entry) {
    setEditId(entry.id); setEditText(entry.text);
    setEditTags(entry.tags || []); setEditMood(entry.mood || null);
  }

  function saveEdit() {
    if (!editText.trim()) return;
    setEntries(p => p.map(e => e.id === editId
      ? { ...e, text: editText.trim(), tags: editTags, mood: editMood, edited: true, updatedAt: new Date().toISOString() }
      : e
    ));
    setEditId(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function delEntry(id) {
    setEntries(p => p.filter(e => e.id !== id));
    setConfirmDel(null);
    if (editId === id) setEditId(null);
  }

  function toggleTag(id, tags, setFn) {
    setFn(p => p.includes(id) ? p.filter(t => t !== id) : [...p, id]);
  }

  const tagCounts = {};
  TAGS.forEach(t => { tagCounts[t.id] = entries.filter(e => (e.tags||[]).includes(t.id)).length; });

  const filtered = entries.filter(e => {
    const mf = filter === 'all' || (e.tags||[]).includes(filter);
    const ms = !search || e.text.toLowerCase().includes(search.toLowerCase());
    return mf && ms;
  });

  const inputStyle = { width:'100%', padding:'8px 12px', background:dark, border:`1px solid ${cyan}44`, borderRadius:6, color:text, fontSize:12, fontFamily:'inherit', outline:'none', boxSizing:'border-box' };
  const taStyle = { ...inputStyle, minHeight:100, resize:'vertical', lineHeight:1.7, fontSize:13 };
  const tagBtn = (color, active) => ({ padding:'3px 10px', borderRadius:4, fontSize:9, fontWeight:700, letterSpacing:1, cursor:'pointer', border:`1px solid ${active?color:border}`, background:active?`${color}22`:'transparent', color:active?color:muted });
  const moodBtn = (active) => ({ background:'none', border:'none', fontSize:18, cursor:'pointer', opacity:active?1:0.3, transition:'opacity 0.15s', padding:'2px 3px' });
  const actionBtn = (primary) => ({ padding:'7px 18px', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', border:primary?'none':`1px solid ${border}`, background:primary?`linear-gradient(135deg,${blue},${cyan})`:'transparent', color:primary?'#fff':muted });
  const iconBtn = { background:'none', border:'none', cursor:'pointer', fontSize:13, padding:'2px 5px', borderRadius:4 };

  return (
    <div style={{ padding:20, maxWidth:860, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:16, fontWeight:700, color:cyan, letterSpacing:1 }}>TRADING JOURNAL</span>
          <span style={{ fontSize:10, color:muted }}>{entries.length} {entries.length===1?'entry':'entries'}</span>
          {saved && <span style={{ fontSize:10, color:'#00d464', fontWeight:700 }}>✓ Saved</span>}
        </div>
        <button
          style={{ padding:'7px 16px', background:`linear-gradient(135deg,${blue},${cyan})`, border:'none', borderRadius:7, color:'#fff', fontSize:11, fontWeight:700, letterSpacing:1, cursor:'pointer' }}
          onClick={() => setShowNew(v => !v)}
        >
          {showNew ? '✕ Cancel' : '+ New Entry'}
        </button>
      </div>

      {/* Stats */}
      {entries.length > 0 && (
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          {TAGS.filter(t => tagCounts[t.id] > 0).map(t => (
            <div key={t.id} style={{ background:dark, border:`1px solid ${t.color}33`, borderRadius:7, padding:'6px 12px', textAlign:'center', minWidth:70 }}>
              <div style={{ fontSize:18, fontWeight:700, color:t.color, fontFamily:'monospace' }}>{tagCounts[t.id]}</div>
              <div style={{ fontSize:8, color:muted, letterSpacing:1, textTransform:'uppercase' }}>{t.label.split(' ')[0]}</div>
            </div>
          ))}
        </div>
      )}

      {/* New entry */}
      {showNew && (
        <div style={{ background:dark, border:`1px solid ${cyan}`, borderRadius:10, padding:16, marginBottom:16 }}>
          <div style={{ fontSize:10, color:cyan, fontWeight:700, letterSpacing:1, marginBottom:10 }}>NEW ENTRY</div>
          <textarea
            style={taStyle}
            placeholder="What happened today? What did you learn? How did you feel about your trades?"
            value={newText}
            onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key==='Enter' && e.ctrlKey) addEntry(); }}
            autoFocus
          />
          <div style={{ fontSize:9, color:muted, marginTop:3 }}>Ctrl+Enter to save quickly</div>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:10, alignItems:'center' }}>
            <span style={{ fontSize:10, color:muted }}>TAG:</span>
            {TAGS.map(t => <button key={t.id} style={tagBtn(t.color, newTags.includes(t.id))} onClick={() => toggleTag(t.id, newTags, setNewTags)}>{t.label.toUpperCase()}</button>)}
          </div>
          <div style={{ display:'flex', gap:4, alignItems:'center', marginTop:8 }}>
            <span style={{ fontSize:10, color:muted, marginRight:4 }}>MOOD:</span>
            {MOODS.map(m => <button key={m} style={moodBtn(newMood===m)} onClick={() => setNewMood(p => p===m?null:m)}>{m}</button>)}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
            <button style={actionBtn(false)} onClick={() => { setShowNew(false); setNewText(''); setNewTags([]); setNewMood(null); }}>Cancel</button>
            <button style={actionBtn(true)} onClick={addEntry} disabled={!newText.trim()}>Save Entry</button>
          </div>
        </div>
      )}

      {/* Search + filter */}
      {entries.length > 0 && (
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          <input
            style={{ ...inputStyle, flex:1, minWidth:150, fontSize:12 }}
            placeholder="Search entries..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {[{id:'all',label:'ALL',color:cyan}, ...TAGS.filter(t => tagCounts[t.id]>0)].map(t => (
            <button key={t.id}
              style={{ padding:'5px 11px', borderRadius:6, fontSize:9, fontWeight:700, letterSpacing:1, cursor:'pointer', border:`1px solid ${filter===t.id?(t.color||cyan):border}`, background:filter===t.id?`${t.color||cyan}18`:'transparent', color:filter===t.id?(t.color||cyan):muted }}
              onClick={() => setFilter(p => p===t.id&&t.id!=='all'?'all':t.id)}
            >
              {t.label?.split(' ')[0].toUpperCase()||'ALL'}
            </button>
          ))}
        </div>
      )}

      {/* Entry list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 20px', color:muted, fontSize:13 }}>
          {entries.length === 0
            ? 'No journal entries yet.\nClick "+ New Entry" to start logging your trading sessions.'
            : 'No entries match your search or filter.'}
        </div>
      ) : filtered.map(entry => (
        <div key={entry.id} style={{ background:panel, border:`1px solid ${editId===entry.id?cyan+'55':border}`, borderRadius:10, padding:16, marginBottom:10, position:'relative' }}>

          {editId !== entry.id ? (
            /* ── View mode ── */
            <>
              <div style={{ position:'absolute', top:10, right:10, display:'flex', gap:4 }}>
                <button style={{ ...iconBtn, color:cyan }} title="Edit entry" onClick={() => startEdit(entry)}>✏️</button>
                <button style={{ ...iconBtn, color:'#ff4444' }} title="Delete entry" onClick={() => setConfirmDel(entry.id)}>🗑</button>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap', paddingRight:60 }}>
                <span style={{ fontSize:10, color:muted, fontFamily:'monospace' }}>{fmtDate(entry.createdAt||entry.date)}</span>
                {entry.edited && <span style={{ fontSize:9, color:muted, fontStyle:'italic' }}>· edited {fmtDate(entry.updatedAt)}</span>}
                {entry.mood && <span style={{ fontSize:16 }}>{entry.mood}</span>}
              </div>
              <div style={{ fontSize:13, color:text, lineHeight:1.75, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{entry.text}</div>
              {(entry.tags||[]).length > 0 && (
                <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:10 }}>
                  {(entry.tags||[]).map(tid => {
                    const t = TAGS.find(x => x.id===tid);
                    return t ? <span key={tid} style={{ ...tagBtn(t.color,true), cursor:'default' }}>{t.label.toUpperCase()}</span> : null;
                  })}
                </div>
              )}
            </>
          ) : (
            /* ── Edit mode ── */
            <>
              <div style={{ fontSize:10, color:cyan, fontWeight:700, letterSpacing:1, marginBottom:8 }}>EDITING</div>
              <div style={{ fontSize:10, color:muted, fontFamily:'monospace', marginBottom:8 }}>{fmtDate(entry.createdAt||entry.date)}</div>
              <textarea
                ref={editRef}
                style={taStyle}
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => { if (e.key==='Enter'&&e.ctrlKey) saveEdit(); if (e.key==='Escape') setEditId(null); }}
              />
              <div style={{ fontSize:9, color:muted, marginTop:3 }}>Ctrl+Enter to save · Escape to cancel</div>
              <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:10, alignItems:'center' }}>
                <span style={{ fontSize:10, color:muted }}>TAG:</span>
                {TAGS.map(t => <button key={t.id} style={tagBtn(t.color, editTags.includes(t.id))} onClick={() => toggleTag(t.id, editTags, setEditTags)}>{t.label.toUpperCase()}</button>)}
              </div>
              <div style={{ display:'flex', gap:4, alignItems:'center', marginTop:8 }}>
                <span style={{ fontSize:10, color:muted, marginRight:4 }}>MOOD:</span>
                {MOODS.map(m => <button key={m} style={moodBtn(editMood===m)} onClick={() => setEditMood(p => p===m?null:m)}>{m}</button>)}
              </div>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
                <button style={{ ...actionBtn(false), color:'#ff4444', borderColor:'rgba(255,68,68,0.3)' }} onClick={() => setConfirmDel(entry.id)}>Delete</button>
                <button style={actionBtn(false)} onClick={() => setEditId(null)}>Cancel</button>
                <button style={actionBtn(true)} onClick={saveEdit} disabled={!editText.trim()}>Save Changes</button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* Delete confirm modal */}
      {confirmDel && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setConfirmDel(null)}>
          <div style={{ background:panel, border:'1px solid rgba(255,68,68,0.4)', borderRadius:12, padding:28, maxWidth:300, textAlign:'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:32, marginBottom:10 }}>🗑️</div>
            <div style={{ fontSize:15, fontWeight:700, color:text, marginBottom:6 }}>Delete this entry?</div>
            <div style={{ fontSize:12, color:muted, marginBottom:20 }}>This cannot be undone.</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button style={actionBtn(false)} onClick={() => setConfirmDel(null)}>Keep It</button>
              <button style={{ ...actionBtn(true), background:'linear-gradient(135deg,#cc2222,#ff4444)' }} onClick={() => delEntry(confirmDel)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
